
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Trash2, Edit, Save, X, Check, AlertTriangle, Play } from 'lucide-react';
import { toast } from 'sonner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { 
  fetchRegexPatterns, 
  createRegexPattern, 
  updateRegexPattern, 
  deleteRegexPattern, 
  testRegexPattern,
  RegexPattern 
} from '@/services/ai-regex-service';

export const RegexPatternsManagement: React.FC = () => {
  const [patterns, setPatterns] = useState<RegexPattern[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingPattern, setEditingPattern] = useState<RegexPattern | null>(null);
  const [newPattern, setNewPattern] = useState<Partial<RegexPattern>>({
    name: '',
    pattern: '',
    description: '',
    is_active: true,
    metadata: { priority: 10, type: 'extraction' }
  });
  const [isAddingNew, setIsAddingNew] = useState(false);
  
  // Test pattern state
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [testPatternData, setTestPatternData] = useState<{
    id: string;
    pattern: string;
    testString: string;
  } | null>(null);
  const [testResults, setTestResults] = useState<{
    matches: boolean;
    groups: string[];
  } | null>(null);

  // Fetch patterns from database
  useEffect(() => {
    const loadPatterns = async () => {
      setLoading(true);
      try {
        const data = await fetchRegexPatterns();
        setPatterns(data);
      } catch (error) {
        console.error('Error loading regex patterns:', error);
        toast.error('Failed to load regex patterns');
      } finally {
        setLoading(false);
      }
    };

    loadPatterns();
  }, []);

  // Filter patterns based on search query
  const filteredPatterns = patterns.filter(
    pattern =>
      pattern.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      pattern.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      pattern.pattern.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Save a new pattern
  const handleSaveNewPattern = async () => {
    if (!newPattern.name || !newPattern.pattern) {
      toast.error('Name and pattern are required');
      return;
    }

    try {
      // Validate pattern
      try {
        new RegExp(newPattern.pattern);
      } catch (e) {
        toast.error(`Invalid regex pattern: ${e.message}`);
        return;
      }

      const createdPattern = await createRegexPattern({
        name: newPattern.name,
        pattern: newPattern.pattern,
        description: newPattern.description || '',
        is_active: newPattern.is_active !== false,
        metadata: newPattern.metadata || { type: 'extraction', priority: 10 }
      });

      setPatterns([...patterns, createdPattern]);
      setNewPattern({
        name: '',
        pattern: '',
        description: '',
        is_active: true,
        metadata: { priority: 10, type: 'extraction' }
      });
      setIsAddingNew(false);
      toast.success('Pattern added successfully');
    } catch (error) {
      console.error('Error adding pattern:', error);
      toast.error(`Failed to add pattern: ${error.message}`);
    }
  };

  // Update an existing pattern
  const handleUpdatePattern = async () => {
    if (!editingPattern || !editingPattern.id) return;

    if (!editingPattern.name || !editingPattern.pattern) {
      toast.error('Name and pattern are required');
      return;
    }

    try {
      const updatedPattern = await updateRegexPattern(editingPattern.id, {
        name: editingPattern.name,
        pattern: editingPattern.pattern,
        description: editingPattern.description,
        is_active: editingPattern.is_active,
        metadata: editingPattern.metadata
      });

      setPatterns(
        patterns.map(pattern =>
          pattern.id === updatedPattern.id ? updatedPattern : pattern
        )
      );
      setEditingPattern(null);
      toast.success('Pattern updated successfully');
    } catch (error) {
      console.error('Error updating pattern:', error);
      toast.error(`Failed to update pattern: ${error.message}`);
    }
  };

  // Delete a pattern
  const handleDeletePattern = async (id: string) => {
    try {
      await deleteRegexPattern(id);
      setPatterns(patterns.filter(pattern => pattern.id !== id));
      toast.success('Pattern deleted successfully');
    } catch (error) {
      console.error('Error deleting pattern:', error);
      toast.error(`Failed to delete pattern: ${error.message}`);
    }
  };

  // Toggle pattern active status
  const handleToggleActive = async (pattern: RegexPattern) => {
    try {
      const updatedPattern = await updateRegexPattern(pattern.id, {
        is_active: !pattern.is_active
      });
      
      setPatterns(
        patterns.map(p =>
          p.id === updatedPattern.id ? updatedPattern : p
        )
      );
      
      toast.success(`Pattern ${updatedPattern.is_active ? 'activated' : 'deactivated'} successfully`);
    } catch (error) {
      console.error('Error toggling pattern status:', error);
      toast.error(`Failed to update status: ${error.message}`);
    }
  };

  // Test a pattern
  const handleOpenTestDialog = (pattern: RegexPattern) => {
    setTestPatternData({
      id: pattern.id,
      pattern: pattern.pattern,
      testString: pattern.metadata?.example || 'SUGGESTED_QUESTIONS:\n- First question\n- Second question\n- Third question'
    });
    setTestResults(null);
    setShowTestDialog(true);
  };

  const handleTestPattern = () => {
    if (!testPatternData) return;
    
    try {
      const results = testRegexPattern(
        testPatternData.pattern,
        testPatternData.testString
      );
      
      setTestResults(results);
    } catch (error) {
      console.error('Error testing pattern:', error);
      toast.error(`Test failed: ${error.message}`);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <Input
          placeholder="Search regex patterns..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          className="max-w-md"
        />
        <Button onClick={() => setIsAddingNew(true)} disabled={isAddingNew}>
          <Plus className="mr-2 h-4 w-4" /> Add New Pattern
        </Button>
      </div>

      {isAddingNew && (
        <Card className="mb-6 border-dashed">
          <CardContent className="pt-6">
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">Name</label>
                  <Input
                    value={newPattern.name}
                    onChange={e =>
                      setNewPattern({ ...newPattern, name: e.target.value })
                    }
                    placeholder="unique_pattern_name"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-1 block">
                      Priority
                    </label>
                    <Input
                      type="number"
                      value={newPattern.metadata?.priority || 10}
                      onChange={e =>
                        setNewPattern({
                          ...newPattern,
                          metadata: {
                            ...newPattern.metadata,
                            priority: parseInt(e.target.value) || 10
                          }
                        })
                      }
                      placeholder="10"
                    />
                  </div>
                  <div className="flex items-center pt-6">
                    <Switch
                      checked={newPattern.is_active !== false}
                      onCheckedChange={(checked) =>
                        setNewPattern({ ...newPattern, is_active: checked })
                      }
                      id="new-pattern-active"
                    />
                    <label htmlFor="new-pattern-active" className="ml-2">
                      Active
                    </label>
                  </div>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">
                  Pattern (Regex)
                </label>
                <Textarea
                  rows={3}
                  value={newPattern.pattern}
                  onChange={e =>
                    setNewPattern({ ...newPattern, pattern: e.target.value })
                  }
                  placeholder="Regular expression pattern"
                  className="font-mono text-sm"
                />
                <p className="text-xs mt-1 text-muted-foreground">
                  Use valid JavaScript RegExp format. Be careful with escape characters.
                </p>
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">
                  Description
                </label>
                <Textarea
                  value={newPattern.description || ''}
                  onChange={e =>
                    setNewPattern({ ...newPattern, description: e.target.value })
                  }
                  placeholder="Description of what this pattern matches"
                  rows={2}
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">
                  Example Text
                </label>
                <Textarea
                  value={newPattern.metadata?.example || ''}
                  onChange={e =>
                    setNewPattern({
                      ...newPattern,
                      metadata: {
                        ...newPattern.metadata,
                        example: e.target.value
                      }
                    })
                  }
                  placeholder="Example text that this pattern should match"
                  rows={3}
                />
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setIsAddingNew(false)}
                >
                  <X className="mr-2 h-4 w-4" /> Cancel
                </Button>
                <Button onClick={handleSaveNewPattern}>
                  <Save className="mr-2 h-4 w-4" /> Save Pattern
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="text-center py-4">Loading regex patterns...</div>
      ) : filteredPatterns.length === 0 ? (
        <div className="text-center py-4 text-muted-foreground">
          {searchQuery
            ? 'No patterns match your search'
            : 'No regex patterns found. Add your first pattern above.'}
        </div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Pattern</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="w-28">Status</TableHead>
                <TableHead className="w-36">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPatterns.map(pattern => (
                <TableRow key={pattern.id} className={!pattern.is_active ? 'opacity-60' : ''}>
                  <TableCell className="font-medium">
                    {editingPattern?.id === pattern.id ? (
                      <Input
                        value={editingPattern.name}
                        onChange={e =>
                          setEditingPattern({
                            ...editingPattern,
                            name: e.target.value,
                          })
                        }
                      />
                    ) : (
                      <div className="flex items-center">
                        {pattern.name}
                        {pattern.metadata?.priority && (
                          <Badge variant="outline" className="ml-2">
                            P{pattern.metadata.priority}
                          </Badge>
                        )}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    {editingPattern?.id === pattern.id ? (
                      <Textarea
                        value={editingPattern.pattern}
                        onChange={e =>
                          setEditingPattern({
                            ...editingPattern,
                            pattern: e.target.value,
                          })
                        }
                        rows={3}
                        className="font-mono text-sm"
                      />
                    ) : (
                      <div className="max-h-16 overflow-y-auto">
                        {pattern.pattern.length > 60
                          ? `${pattern.pattern.substring(0, 60)}...`
                          : pattern.pattern}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    {editingPattern?.id === pattern.id ? (
                      <Textarea
                        value={editingPattern.description || ''}
                        onChange={e =>
                          setEditingPattern({
                            ...editingPattern,
                            description: e.target.value,
                          })
                        }
                        rows={2}
                      />
                    ) : (
                      <div className="max-h-16 overflow-y-auto">
                        {pattern.description}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center">
                      {editingPattern?.id === pattern.id ? (
                        <Switch
                          checked={editingPattern.is_active}
                          onCheckedChange={(checked) =>
                            setEditingPattern({
                              ...editingPattern,
                              is_active: checked,
                            })
                          }
                        />
                      ) : (
                        <Switch
                          checked={pattern.is_active}
                          onCheckedChange={() => handleToggleActive(pattern)}
                        />
                      )}
                      <span className="ml-2">
                        {pattern.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {editingPattern?.id === pattern.id ? (
                      <div className="flex space-x-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={handleUpdatePattern}
                        >
                          <Save className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setEditingPattern(null)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex space-x-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenTestDialog(pattern)}
                          title="Test pattern"
                        >
                          <Play className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setEditingPattern({ ...pattern })}
                          title="Edit pattern"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" title="Delete pattern">
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>
                                Delete Pattern
                              </AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete this regex pattern? This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDeletePattern(pattern.id)}
                                className="bg-destructive hover:bg-destructive/90"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Pattern Testing Dialog */}
      <Dialog open={showTestDialog} onOpenChange={setShowTestDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Test Regex Pattern</DialogTitle>
            <DialogDescription>
              Test how this regex pattern matches against sample text
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 my-2">
            <div>
              <label className="text-sm font-medium mb-1 block">Pattern</label>
              <Textarea
                value={testPatternData?.pattern || ''}
                onChange={e => testPatternData && setTestPatternData({
                  ...testPatternData,
                  pattern: e.target.value
                })}
                className="font-mono text-sm"
                rows={2}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium mb-1 block">Test Text</label>
              <Textarea
                value={testPatternData?.testString || ''}
                onChange={e => testPatternData && setTestPatternData({
                  ...testPatternData,
                  testString: e.target.value
                })}
                rows={5}
              />
            </div>
            
            <Button onClick={handleTestPattern} className="w-full">
              <Play className="mr-2 h-4 w-4" /> Test Pattern
            </Button>
            
            {testResults && (
              <div className="mt-4 p-4 border rounded-md">
                <div className="flex items-center mb-2">
                  <h3 className="text-lg font-medium">Results</h3>
                  {testResults.matches ? (
                    <Badge className="ml-2 bg-green-600">Match Found</Badge>
                  ) : (
                    <Badge className="ml-2 bg-red-600">No Match</Badge>
                  )}
                </div>
                
                {testResults.matches && testResults.groups.length > 0 && (
                  <div className="mt-2">
                    <h4 className="text-sm font-medium mb-1">Captured Groups:</h4>
                    <ul className="space-y-1 text-sm">
                      {testResults.groups.map((group, i) => (
                        <li key={i} className="border p-2 rounded bg-muted/30">
                          {group}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {testResults.matches && testResults.groups.length === 0 && (
                  <div className="text-sm text-amber-600 flex items-center">
                    <AlertTriangle className="h-4 w-4 mr-1" /> Pattern matched but no capture groups were found
                  </div>
                )}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTestDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Help Accordion */}
      <Accordion type="single" collapsible className="mt-8">
        <AccordionItem value="help">
          <AccordionTrigger>Pattern Writing Help & Tips</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4 text-sm">
              <div>
                <h3 className="font-medium">Regular Expression Basics</h3>
                <ul className="list-disc pl-5 mt-2 space-y-1">
                  <li><code className="bg-muted px-1 rounded">.</code> - Matches any character</li>
                  <li><code className="bg-muted px-1 rounded">*</code> - Matches 0 or more of the preceding character</li>
                  <li><code className="bg-muted px-1 rounded">+</code> - Matches 1 or more of the preceding character</li>
                  <li><code className="bg-muted px-1 rounded">?</code> - Makes the preceding character optional</li>
                  <li><code className="bg-muted px-1 rounded">\s</code> - Matches whitespace</li>
                  <li><code className="bg-muted px-1 rounded">\n</code> - Matches newline</li>
                  <li><code className="bg-muted px-1 rounded">()</code> - Creates a capture group</li>
                </ul>
              </div>
              
              <div>
                <h3 className="font-medium">Extraction Pattern Tips</h3>
                <ul className="list-disc pl-5 mt-2 space-y-1">
                  <li>Use capture groups <code className="bg-muted px-1 rounded">()</code> to extract the suggestions</li>
                  <li>For multiple alternatives, use <code className="bg-muted px-1 rounded">|</code> like <code className="bg-muted px-1 rounded">(-|\*|1\.)</code></li>
                  <li>For multiline patterns, use the <code className="bg-muted px-1 rounded">s</code> flag (added automatically in the test function)</li>
                </ul>
              </div>
              
              <div>
                <h3 className="font-medium">Example Patterns</h3>
                <div className="mt-2 space-y-2">
                  <div>
                    <div className="font-medium">Dash Format</div>
                    <code className="bg-muted p-2 text-xs block rounded overflow-x-auto">
                      SUGGESTED_QUESTIONS:(?:\s*\n|\s+)?(?:-\s*(.*?)(?:\n|$)\s*)?(?:-\s*(.*?)(?:\n|$)\s*)?(?:-\s*(.*?)(?:\n|$)\s*)?
                    </code>
                  </div>
                  <div>
                    <div className="font-medium">Numbered Format</div>
                    <code className="bg-muted p-2 text-xs block rounded overflow-x-auto">
                      SUGGESTED_QUESTIONS:(?:\s*\n|\s+)?(?:1\.?\s*(.*?)(?:\n|$)\s*)?(?:2\.?\s*(.*?)(?:\n|$)\s*)?(?:3\.?\s*(.*?)(?:\n|$)\s*)?
                    </code>
                  </div>
                </div>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};
